import { memo, useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { useTranslation } from '@hooks/useTranslation';
import TextInput from '@components/ui/Input/TextInput';
import { Button } from '@components/ui';
import CloseIcon from '@assets/icons/CloseIcon';
import type { FilterFieldConfig, FilterValue, FilterValues } from '@/types/filters';
import FilterField from './FilterField';
import styles from './FilterPanel.module.scss';

// ============================================
// SEARCH ICON
// ============================================

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// TYPES
// ============================================

export interface FilterPanelProps {
  /** Whether the panel is visible */
  isOpen: boolean;
  /** Close the panel */
  onClose: () => void;
  /** Filter field configurations */
  fields: FilterFieldConfig[];
  /** Currently applied filter values (from URL) */
  values: FilterValues;
  /** Called when user clicks Apply */
  onApply: (values: FilterValues) => void;
  /** Called when user clicks Reset */
  onReset: () => void;
  /** Panel title */
  title?: string;
}

// ============================================
// COMPONENT
// ============================================

function FilterPanel({
  isOpen,
  onClose,
  fields,
  values,
  onApply,
  onReset,
  title: titleProp,
}: FilterPanelProps) {
  const { t } = useTranslation();
  const title = titleProp ?? t('K_FILTER_TITLE', 'Filters');
  const panelRef = useRef<HTMLDivElement>(null);

  // Draft state — local copy of filters, only committed on Apply
  const [draft, setDraft] = useState<FilterValues>({});
  const [searchQuery, setSearchQuery] = useState('');

  // Sync draft with external values when panel opens
  useEffect(() => {
    if (isOpen) {
      setDraft({ ...values });
      setSearchQuery('');
    }
  }, [isOpen, values]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Filter visible fields by search query (matches against label text)
  const visibleFields = useMemo(() => {
    if (!searchQuery.trim()) return fields;
    const q = searchQuery.toLowerCase();
    return fields.filter((f) => f.label.toLowerCase().includes(q));
  }, [fields, searchQuery]);

  // Update a single draft field
  const handleFieldChange = useCallback((fieldKey: string, value: FilterValue | undefined) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (value === undefined) {
        delete next[fieldKey];
      } else {
        next[fieldKey] = value;
      }
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    onApply(draft);
    onClose();
  }, [draft, onApply, onClose]);

  const handleReset = useCallback(() => {
    setDraft({});
    onReset();
    onClose();
  }, [onReset, onClose]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // Backdrop click
  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={styles.backdrop}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        ref={panelRef}
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="dialog"
        aria-label={title}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>{title}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('K_ARIA_CLOSE_FILTERS', 'Close filters')}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Search input */}
        <div className={styles.searchWrapper}>
          <TextInput
            placeholder={t('K_PLACEHOLDER_SEARCH_FILTERS', 'Search filters...')}
            value={searchQuery}
            onChange={handleSearchChange}
            size="sm"
            leftIcon={<SearchIcon />}
          />
        </div>

        {/* Fields */}
        <div className={styles.fieldsContainer}>
          {visibleFields.length === 0 ? (
            <p className={styles.noResults}>{t('K_FILTER_NO_MATCHING', 'No matching filters')}</p>
          ) : (
            visibleFields.map((field) => (
              <FilterField
                key={field.fieldKey}
                config={field}
                value={draft[field.fieldKey]}
                isApplied={field.fieldKey in values}
                onChange={(val) => handleFieldChange(field.fieldKey, val)}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className={styles.panelFooter}>
          <Button variant="outline" size="md" onClick={handleReset}>
            {t('K_RESET', 'Reset')}
          </Button>
          <Button variant="filled" size="md" onClick={handleApply}>
            {t('K_APPLY', 'Apply')}
          </Button>
        </div>
      </div>
    </>
  );
}

export default memo(FilterPanel);
